'use strict';

import { Entity } from '../Entity';
import { GlobCompanies } from '../Glob/GlobCompanies';
import { CommissionStatementMapping } from './CommissionStatementMapping';

/**
 * Commission Statement Mapping Company Entity
 *
 * Represents a mapping between a commission statement mapping and a specific
 * insurance company. This entity defines how company names or identifiers
 * in commission statement files should be mapped to standardized company
 * records in the system. It includes referenced values that are used to
 * identify the company in various commission statement formats.
 */
export class CommissionStatementMappingCompany extends Entity {
  /** Unique identifier for the mapping company */
  protected id: number;

  /** The commission statement mapping this company mapping belongs to */
  protected commissionStatementMapping: CommissionStatementMapping;

  /** The standardized company record this mapping points to */
  protected company: GlobCompanies;

  /** Array of string values that identify this company in commission statements */
  protected referencedValues: Array<string>;

  /**
   * Constructor
   *
   * Initializes a new CommissionStatementMappingCompany instance
   */
  constructor() {
    super();
  }

  /**
   * Hydrate the entity with data from API response
   *
   * Populates the mapping company entity with data from the API,
   * including related entities like the commission statement mapping
   * and the associated company record.
   *
   * @param data - Raw data object from API
   */
  public hydrate(data: any) {
    // Basic properties
    this.id = data.id;
    this.referencedValues = data.referencedValues;

    // Hydrate related CommissionStatementMapping entity
    if (data.commissionStatementMapping) {
      const commissionStatementMapping = new CommissionStatementMapping();
      commissionStatementMapping.hydrate(data.commissionStatementMapping);
      this.commissionStatementMapping = commissionStatementMapping;
    }

    // Hydrate related GlobCompanies entity
    if (data.company) {
      const company = new GlobCompanies();
      company.hydrate(data.company);
      this.company = company;
    }
  }

  // Getter and Setter Methods

  /**
   * Get the mapping company ID
   *
   * @returns The unique identifier
   */
  public getId(): number {
    return this.id;
  }

  /**
   * Get the referenced values
   *
   * Returns the array of string values that are used to identify
   * this company in commission statement files. These values are
   * matched against company names or identifiers in the raw data.
   *
   * @returns Array of string values used to identify the company
   */
  public getReferencedValues(): Array<string> {
    return this.referencedValues;
  }

  /**
   * Set the referenced values
   *
   * Sets the array of string values that should be used to identify
   * this company in commission statement files. When processing
   * commission statements, these values will be matched against
   * company names or identifiers in the data.
   *
   * @param referencedValues - Array of string values to identify the company
   * @returns This instance for method chaining
   */
  public setReferencedValues(referencedValues: Array<string>): this {
    this.referencedValues = referencedValues;
    return this;
  }

  /**
   * Get the associated commission statement mapping
   *
   * @returns The commission statement mapping this company mapping belongs to
   */
  public getCommissionStatementMapping(): CommissionStatementMapping {
    return this.commissionStatementMapping;
  }

  /**
   * Set the associated commission statement mapping
   *
   * @param commissionStatementMapping - The commission statement mapping
   * @returns This instance for method chaining
   */
  public setCommissionStatementMapping(
    commissionStatementMapping: CommissionStatementMapping
  ): this {
    this.commissionStatementMapping = commissionStatementMapping;
    return this;
  }

  /**
   * Get the associated company
   *
   * @returns The standardized company record this mapping points to
   */
  public getCompany(): GlobCompanies {
    return this.company;
  }

  /**
   * Set the associated company
   *
   * @param company - The standardized company record
   * @returns This instance for method chaining
   */
  public setCompany(company: GlobCompanies): this {
    this.company = company;
    return this;
  }
}
